import React from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Stack,
} from '@mui/joy'
import Grid from '@mui/joy/Grid'
import {
  TrendUp as TrendingUp,
  TrendDown as TrendingDown,
  ArrowRight as TrendingFlat,
} from '@phosphor-icons/react'
import { useSalesOverview } from '../../../hooks/useSalesReports'
import type { DateRange } from '../../../types/salesReports'

interface SalesOverviewProps {
  dateRange: DateRange
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

const formatNumber = (num: number, decimals = 1) => {
  return num.toLocaleString('en-US', { maximumFractionDigits: decimals })
}

interface MetricCardProps {
  title: string
  value: string | number
  subtitle?: string
  changeVsPrevious?: number
  changeVsLastYear?: number
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'neutral'
  format?: 'currency' | 'percent' | 'number' | 'days'
}

function MetricCard({
  title,
  value,
  subtitle,
  changeVsPrevious,
  changeVsLastYear,
  color = 'primary',
  format = 'number'
}: MetricCardProps) {
  const formatValue = (val: string | number) => {
    const numVal = typeof val === 'string' ? parseFloat(val) : val
    switch (format) {
      case 'currency':
        return formatCurrency(numVal)
      case 'percent':
        return `${formatNumber(numVal)}%`
      case 'days':
        return `${formatNumber(numVal)} days`
      default:
        return formatNumber(numVal, 0)
    }
  }

  const renderChange = (change: number | undefined, label: string) => {
    if (change === undefined || change === null) return null

    const isPositive = change > 0
    const isNegative = change < 0
    const Icon = isPositive ? TrendingUp : isNegative ? TrendingDown : TrendingFlat
    const chipColor = isPositive ? 'success' : isNegative ? 'danger' : 'neutral'

    return (
      <Chip
        size="sm"
        variant="soft"
        color={chipColor}
        startDecorator={<Icon size={14} />}
      >
        {isPositive ? '+' : ''}{formatNumber(change)}% {label}
      </Chip>
    )
  }

  return (
    <Card variant="soft" color={color}>
      <CardContent>
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          {title}
        </Typography>
        <Typography level="h3" sx={{ my: 1 }}>
          {formatValue(value)}
        </Typography>
        {subtitle && (
          <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {renderChange(changeVsPrevious, 'vs prev')}
          {renderChange(changeVsLastYear, 'vs LY')}
        </Stack>
      </CardContent>
    </Card>
  )
}

export default function SalesOverview({ dateRange }: SalesOverviewProps) {
  const { data, isLoading, error } = useSalesOverview({
    date_from: dateRange.from,
    date_to: dateRange.to,
  })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert color="danger">
        Failed to load overview data. Please try again.
      </Alert>
    )
  }

  if (!data) {
    return (
      <Alert color="warning">
        No data available for the selected period.
      </Alert>
    )
  }

  const { metrics, comparisons, jobs_revenue } = data
  const vsPrev = comparisons.vs_previous_period
  const vsLY = comparisons.vs_last_year

  return (
    <Box>
      <Typography level="title-lg" sx={{ mb: 3 }}>
        Sales Overview
      </Typography>

      {/* Pipeline Metrics */}
      <Typography level="title-md" sx={{ mb: 2, color: 'text.secondary' }}>
        Pipeline Health
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Pipeline"
            value={metrics.total_pipeline}
            subtitle={`${metrics.total_opportunities} opportunities`}
            changeVsPrevious={vsPrev.total_pipeline}
            changeVsLastYear={vsLY.total_pipeline}
            color="primary"
            format="currency"
          />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <MetricCard
            title="Weighted Pipeline"
            value={metrics.weighted_pipeline}
            subtitle="Adjusted for probability"
            changeVsPrevious={vsPrev.weighted_pipeline}
            changeVsLastYear={vsLY.weighted_pipeline}
            color="success"
            format="currency"
          />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <MetricCard
            title="Win Rate"
            value={metrics.win_rate}
            subtitle={`${metrics.deals_won} won, ${metrics.deals_lost} lost`}
            changeVsPrevious={vsPrev.win_rate}
            changeVsLastYear={vsLY.win_rate}
            color="warning"
            format="percent"
          />
        </Grid>
        <Grid xs={12} sm={6} md={3}>
          <MetricCard
            title="Avg Sales Cycle"
            value={metrics.avg_sales_cycle}
            subtitle="Days to close"
            changeVsPrevious={vsPrev.avg_sales_cycle}
            changeVsLastYear={vsLY.avg_sales_cycle}
            color="neutral"
            format="days"
          />
        </Grid>
      </Grid>

      {/* Revenue Metrics */}
      <Typography level="title-md" sx={{ mb: 2, color: 'text.secondary' }}>
        Revenue Performance
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid xs={12} sm={6} md={4}>
          <MetricCard
            title="Revenue Closed"
            value={metrics.revenue_closed}
            subtitle="From won opportunities"
            changeVsPrevious={vsPrev.revenue_closed}
            changeVsLastYear={vsLY.revenue_closed}
            color="success"
            format="currency"
          />
        </Grid>
        <Grid xs={12} sm={6} md={4}>
          <MetricCard
            title="Avg Deal Size"
            value={metrics.avg_deal_size}
            subtitle="Won deals average"
            changeVsPrevious={vsPrev.avg_deal_size}
            changeVsLastYear={vsLY.avg_deal_size}
            color="primary"
            format="currency"
          />
        </Grid>
        <Grid xs={12} sm={6} md={4}>
          <MetricCard
            title="Job Revenue"
            value={jobs_revenue}
            subtitle="Completed jobs"
            color="success"
            format="currency"
          />
        </Grid>
      </Grid>

      {/* Period Comparison */}
      <Typography level="title-md" sx={{ mb: 2, color: 'text.secondary' }}>
        Period Comparison
      </Typography>
      <Grid container spacing={2}>
        <Grid xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-sm" sx={{ mb: 2 }}>
                Previous Period
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography level="body-sm">Pipeline Value</Typography>
                  <Typography level="body-sm" fontWeight="lg">
                    {formatCurrency(comparisons.previous_period.total_pipeline)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography level="body-sm">Revenue Closed</Typography>
                  <Typography level="body-sm" fontWeight="lg">
                    {formatCurrency(comparisons.previous_period.revenue_closed)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography level="body-sm">Win Rate</Typography>
                  <Typography level="body-sm" fontWeight="lg">
                    {formatNumber(comparisons.previous_period.win_rate)}%
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography level="body-sm">Deals Won</Typography>
                  <Typography level="body-sm" fontWeight="lg">
                    {comparisons.previous_period.deals_won}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-sm" sx={{ mb: 2 }}>
                Same Period Last Year
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography level="body-sm">Pipeline Value</Typography>
                  <Typography level="body-sm" fontWeight="lg">
                    {formatCurrency(comparisons.last_year.total_pipeline)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography level="body-sm">Revenue Closed</Typography>
                  <Typography level="body-sm" fontWeight="lg">
                    {formatCurrency(comparisons.last_year.revenue_closed)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography level="body-sm">Win Rate</Typography>
                  <Typography level="body-sm" fontWeight="lg">
                    {formatNumber(comparisons.last_year.win_rate)}%
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography level="body-sm">Deals Won</Typography>
                  <Typography level="body-sm" fontWeight="lg">
                    {comparisons.last_year.deals_won}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
